import React, { useState, SyntheticEvent, useEffect } from 'react'
import get from 'lodash/get'
import {
  IIntegration,
  IIntegrationApplicationField,
  IEnableField,
  IIntegrationApplication,
} from '@api/integration/types'
import { Tab, Tabs, FormGroup, Classes, Intent, TabId } from '@blueprintjs/core'
import { Button, IconButton, Confirmation, Callout, Dialog } from '@ui'
import IntegrationInputGroup from './IntegrationInputGroup'
import { reduceAppFields, capitalize, isValidEmail } from './utils'

const isFieldInvalid = (field: IIntegrationApplicationField) =>
  (field.required && !field.value) || (field.name === 'email' && !isValidEmail(field.value))

const isFormInvalid = (applications: IIntegrationApplication[], selectedTabId: string) => {
  const app = applications.find(({ name }) => name === selectedTabId)
  if (!app) {
    return false
  }
  const countOfEmptyFields = app.fields.reduce(
    (count: any, field) => (isFieldInvalid(field) ? count + 1 : count),
    0
  )
  return countOfEmptyFields > 0
}

export default function ActiveModal({
  isOpen,
  onClose,
  integration,
  regenerateToken,
  disableIntegration,
  updateIntegration,
  sendSlackInstructions,
}: {
  isOpen: boolean
  onClose: () => void
  integration: IIntegration
  regenerateToken: (id: string) => Promise<IIntegration>
  disableIntegration: (id: string) => Promise<IIntegration>
  updateIntegration: (id: string, sourse: string, fields: IEnableField[]) => Promise<IIntegration>
  sendSlackInstructions: (id: string) => Promise<boolean>
}) {
  const firstAppName = get(integration, 'applications[0].name')
  const [selectedTabId, setSelectedTabId] = useState(firstAppName)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [hasRegenerated, setHasRegenereated] = useState(false)
  const [applications, setApplications] = useState(integration.applications)

  useEffect(() => {
    setApplications(applications)
  }, [applications])

  const onCloseAndReset = () => {
    onClose()
    setDirty(false)
    setError('')
    setHasRegenereated(false)
    setApplications(integration.applications)
  }

  return (
    <Dialog
      style={{ width: 700 }}
      onClose={onCloseAndReset}
      isOpen={isOpen}
      usePortal
      className="bp4-dialog-override"
      title={integration.name}
      canEscapeKeyClose={!dirty}
      canOutsideClickClose={!dirty}
      isCloseButtonShown={false}
      transitionDuration={200}
      toolbar={
        <div className="dialog-buttons">
          <Confirmation
            text="Delete this integration?"
            shouldHide={error.length > 0}
            onConfirm={() => {
              setError('')
              disableIntegration(integration.id)
                .then(() => onCloseAndReset())
                .catch(() => setError('Unable to disable the integration'))
            }}
          >
            <IconButton minimal tooltip="Delete integration" iconName="trash" />
          </Confirmation>
          {dirty ? (
            <Confirmation text="Discard unsaved changes?" onConfirm={onCloseAndReset}>
              <IconButton minimal tooltip="Close" iconName="times" />
            </Confirmation>
          ) : (
            <IconButton minimal tooltip="Close" iconName="times" onClick={onCloseAndReset} />
          )}
        </div>
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          <div
            style={{
              fontSize: 16,
              borderBottom: '1px solid var(--border-color)',
              padding: '0 0 20px 0',
            }}
          >
            {integration.description}
          </div>
          <Tabs selectedTabId={selectedTabId} onChange={(id: string) => setSelectedTabId(id)}>
            {applications.map((application) => (
              <Tab
                key={application.name}
                id={application.name}
                title={capitalize(application.name)}
                panel={
                  <>
                    {error && (
                      <Callout
                        style={{ marginBottom: 10 }}
                        faIcon="exclamation-triangle"
                        intent={Intent.DANGER}
                      >
                        {error}
                      </Callout>
                    )}
                    <ApplicationPanel
                      {...application}
                      loading={loading}
                      integrationId={integration.id}
                      regenerateToken={() => {
                        setHasRegenereated(true)
                        setDirty(true)
                        regenerateToken(integration.id)
                          .then((integration: IIntegration) =>
                            setApplications(integration.applications)
                          )
                          .catch(() => setError('Unable to regenerate the token'))
                      }}
                      setDirty={setDirty}
                      errors={fieldErrors}
                      setErrors={setFieldErrors}
                      setFields={(fields: IIntegrationApplicationField[]) => {
                        setApplications(
                          applications.map((a: IIntegrationApplication) =>
                            a.name === application.name
                              ? {
                                  ...application,
                                  fields,
                                }
                              : a
                          )
                        )
                      }}
                    />
                  </>
                }
              />
            ))}
          </Tabs>
        </div>
      }
      footer={
        <div className={Classes.DIALOG_FOOTER}>
          {hasRegenerated && (
            <Callout faIcon="exclamation-triangle" intent={Intent.WARNING}>
              You will need to update the new token in your provisioning system's administrator
              panel. Save to apply token.
            </Callout>
          )}

          <div className="details__footer">
            {dirty ? (
              <Confirmation text="Discard unsaved changes?" onConfirm={onCloseAndReset}>
                <Button style={{ marginRight: 8 }} type="button" text="Cancel" />
              </Confirmation>
            ) : (
              <Button style={{ marginRight: 8 }} text="Cancel" onClick={onCloseAndReset} />
            )}
            {integration.name.toLowerCase() === 'slack' && (
              <Button
                style={{ marginRight: 8 }}
                text="Resend Instructions"
                onClick={() => {
                  setLoading(true)

                  sendSlackInstructions(integration.id)
                    .then((status: boolean) => {
                      setLoading(false)
                      !status && setError('Unable to activate')
                    })
                    .catch((error) => {
                      setLoading(false)
                      setError(error.toString())
                    })
                }}
              />
            )}
            <Button
              loading={loading}
              disabled={isFormInvalid(applications, selectedTabId) || !dirty}
              intent={Intent.PRIMARY}
              text="Save"
              onClick={() => {
                setLoading(true)
                const fields = reduceAppFields(applications, selectedTabId)
                updateIntegration(integration.id, integration.source, fields)
                  .then(() => {
                    setLoading(false)
                    setDirty(false)
                    onClose()
                  })
                  .catch(() => {
                    setLoading(false)
                    setError('Unable to save integration')
                  })
              }}
            />
          </div>
        </div>
      }
    />
  )
}

const ApplicationPanel = ({
  integrationId,
  description,
  fields,
  errors,
  loading,
  regenerateToken,
  setDirty,
  setFields,
  setErrors,
}: {
  integrationId: string
  description: string[]
  fields: IIntegrationApplicationField[]
  errors: any
  loading: boolean
  regenerateToken: (id: string) => void
  setDirty: (param: boolean) => void
  setFields: (f: IIntegrationApplicationField[]) => void
  setErrors: (error: any) => void
}) => {
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        {description.map((d: string) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {fields.map((field: IIntegrationApplicationField) => (
        <FormGroup
          key={field.name}
          helperText={field.steps.map((s: string) => s)}
          label={field.description}
          labelFor={field.name}
          labelInfo={field.required ? '(required)' : field.readonly ? '(read-only)' : ''}
        >
          <IntegrationInputGroup
            integrationId={integrationId}
            regenerateToken={regenerateToken}
            {...field}
            disabled={loading}
            hasError={errors[field.name]}
            onChange={({ currentTarget: { name, value } }: SyntheticEvent<HTMLInputElement>) => {
              setDirty(true)
              setErrors({
                ...errors,
                [field.name]:
                  (field.required && !value) || (field.name === 'email' && !isValidEmail(value)),
              })
              setFields(fields.map((f) => (f.name === field.name ? { ...f, value } : f)))
            }}
            onFocus={() => setErrors({ ...errors, [field.name]: false })}
            onBlur={() =>
              setErrors({
                ...errors,
                [field.name]:
                  (field.required && !field.value) ||
                  (field.name === 'email' && !isValidEmail(field.value)),
              })
            }
          />
        </FormGroup>
      ))}
    </div>
  )
}
